import { Link, useLocation } from "react-router-dom";
import { Phone, ArrowUpRight, Instagram } from "lucide-react";
import { buildWhatsAppUrl, isWhatsAppUrl } from "../../utils/whatsapp";
import FooterTransformHeading from "./FooterTransformHeading";
import { LANDING_CONTAINER_PADDED, LANDING_INNER } from "../../constants/landingPageLayout";
import { PLATFORM_NAME } from "../../constants/brand";
import BrandWordmark from "../common/BrandWordmark";

const BRAND_GRADIENT =
  "linear-gradient(69.35deg, #4F46E5 0%, #7C3AED 50%, #6366F1 100%)";

// X (Twitter) icon — kept for future use
// eslint-disable-next-line no-unused-vars
const XIcon = ({ className = "w-5 h-5" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// WhatsApp icon — kept for future use
// eslint-disable-next-line no-unused-vars
const WhatsAppIcon = ({ className = "w-5 h-5" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const headingStyle = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 600,
  fontSize: "13px",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
};

const linkStyle = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 400,
  fontSize: "15px",
  lineHeight: "24px",
};

const Footer = () => {
  // WhatsApp link — kept for future use (see commented social links below)
  const whatsAppUrl = buildWhatsAppUrl();
  // eslint-disable-next-line no-unused-vars
  const hasWhatsAppNumber = isWhatsAppUrl(whatsAppUrl);
  const { pathname } = useLocation();

  const isActivePath = (path) =>
    pathname === path || pathname.startsWith(`${path}/`);

  const footerColumns = [
    {
      id: "company",
      title: "COMPANY",
      links: [
        { name: "About Us", href: "/about" },
        { name: "Contact Us", href: "/contact" },
        { name: "Careers", href: "/careers" },
        { name: "Blog", href: "/blog" },
      ],
    },
    {
      id: "product",
      title: "PRODUCT",
      links: [
        { name: "Pricing", href: "/pricing" },
        { name: "Features", href: "/features" },
      ],
    },
    {
      id: "resources",
      title: "RESOURCES",
      links: [
        { name: "FAQs", href: "/faq" },
        { name: "Support Center", href: "/support" },
        { name: "Franchise", href: "/franchise" },
      ],
    },
    {
      id: "legal",
      title: "LEGAL",
      links: [
        { name: "Terms of Service", href: "/terms" },
        { name: "Privacy Policy", href: "/privacy" },
        { name: "Refund Policy", href: "/refund" },
        { name: "Cookie Policy", href: "/cookie-policy" },
      ],
    },
  ];

  const socialLinks = [
    // {
    //   name: "X",
    //   href: "#",
    //   icon: XIcon,
    // },
    // {
    //   name: "WhatsApp",
    //   href: whatsAppUrl,
    //   icon: WhatsAppIcon,
    //   external: hasWhatsAppNumber,
    // },
    {
      name: "Instagram",
      href: "https://www.instagram.com/jobscribe.in?igsh=MXVjbWE0NzJ0cmdzYw==",
      icon: Instagram,
      external: true,
      ariaLabel: "Follow JobScribe on Instagram",
    },
  ];

  return (
    <footer className="relative w-full bg-[#0F172A] overflow-hidden">
      {/* Top gradient line */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.35), transparent)",
        }}
        aria-hidden
      />

      <div
        className={`relative ${LANDING_CONTAINER_PADDED}`}
        style={{
          paddingTop: "41px",
          paddingBottom: "41px",
        }}
      >
        {/* ── Top Section: Background text SVG + CTA Buttons ── */}
        <div className="relative flex flex-col items-center w-full mb-10 lg:mb-[42px]">
          {/* "Transform Your Recruitment Workflow" watermark SVG */}
          <div className="mx-auto flex w-full select-none justify-center">
            <FooterTransformHeading />
          </div>

          {/* Button group — positioned below SVG */}
          <div
            className="flex items-center justify-center mx-auto"
            style={{
              position: "relative",
              zIndex: 2,
              maxWidth: "353px",
              backgroundColor: "#0F172A",
              borderRadius: "28.17px",
              borderBottom: "0.94px solid #6C7489",
              paddingTop: "20px",
              paddingRight: "14px",
              paddingBottom: "16px",
              paddingLeft: "14px",
            }}
          >
            {/* Inner button row */}
            <div
              className="flex items-center"
              style={{ gap: "13.52px" }}
            >
              {/* Login button — light gray solid pill */}
              <Link
                to="/login"
                className="inline-flex items-center justify-center transition-all duration-200 hover:opacity-90 hover:shadow-lg"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 500,
                  fontSize: "14px",
                  height: "39.52px",
                  padding: "0 24px",
                  backgroundColor: "#E2E8F0",
                  color: "#0F172A",
                  border: "0.85px solid #E2E8F0",
                  borderRadius: "17.89px",
                }}
              >
                Login
              </Link>

              {/* Start Free Trial — matches landing hero CTA */}
              <Link
                to="/signup"
                className="group inline-flex shrink-0 items-center gap-0"
                aria-label="Start Free Trial"
              >
                <span
                  className="inline-flex h-[39.52px] items-center justify-center whitespace-nowrap rounded-[17.89px] bg-[#4F46E5] px-[10.44px] text-[13.42px] font-medium leading-none tracking-normal text-white shadow-md transition-colors duration-200 group-hover:bg-[#4338CA]"
                  style={{ fontFamily: '"Inter", sans-serif' }}
                >
                  Start Free Trial
                </span>
                <span className="inline-flex h-[39.52px] w-[39.52px] shrink-0 items-center justify-center rounded-full bg-[#4F46E5] p-[7.46px] text-white shadow-md transition-colors duration-200 group-hover:bg-[#4338CA]">
                  <ArrowUpRight className="h-4 w-4" strokeWidth={2.5} />
                </span>
              </Link>
            </div>
          </div>
        </div>

        {/* ── Main Footer Links Section ── */}
        <div className={`${LANDING_INNER} flex flex-col gap-[22px]`}>
          {/* Grid: Brand left + 4 link columns */}
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-x-6">
            {/* Brand column */}
            <div className="flex flex-col lg:col-span-4 lg:pr-4">
              {/* Logo */}
              <Link
                to="/"
                aria-label={`${PLATFORM_NAME} home`}
                className="inline-flex items-center gap-3 w-fit mb-4 group"
              >
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl shadow-lg"
                  style={{
                    background: BRAND_GRADIENT,
                    boxShadow: "0 6px 20px rgba(79, 70, 229, 0.25)",
                  }}
                >
                  <div className="grid grid-cols-2 gap-0.5">
                    <div className="h-1.5 w-1.5 rounded-sm bg-white" />
                    <div className="h-1.5 w-1.5 rounded-sm bg-white/80" />
                    <div className="h-1.5 w-1.5 rounded-sm bg-white/80" />
                    <div className="h-1.5 w-1.5 rounded-sm bg-white" />
                  </div>
                </div>
                <BrandWordmark
                  accent="footer"
                  className="text-lg font-semibold tracking-tight text-white"
                />
              </Link>

              {/* Tagline */}
              <p
                className="mb-5 max-w-[260px] text-[14px] leading-relaxed text-slate-400"
                style={{ fontFamily: '"Inter", sans-serif' }}
              >
                Automating job fetching for Indian consultancies. Save time and
                scale your operations effortlessly.
              </p>

              {/* Get in Touch + WhatsApp */}
              <div className="flex items-center gap-2.5 flex-wrap">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-full border border-slate-500 text-slate-300 transition-all duration-200 hover:bg-white/10 hover:border-slate-300 hover:text-white text-[13px]"
                  style={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 500,
                    padding: "8px 18px",
                  }}
                >
                  Get in Touch
                </Link>

                {/* <a
                  href={whatsAppUrl}
                  target={hasWhatsAppNumber ? "_blank" : undefined}
                  rel={hasWhatsAppNumber ? "noopener noreferrer" : undefined}
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[13px] font-semibold text-white transition-all duration-200 hover:opacity-90"
                  style={{
                    fontFamily: '"Inter", sans-serif',
                    background:
                      "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                    boxShadow: "0 4px 14px rgba(16, 185, 129, 0.25)",
                  }}
                  aria-label={`Chat with ${PLATFORM_NAME} on WhatsApp`}
                >
                  <WhatsAppIcon className="h-4 w-4 shrink-0" />
                  Chat on WhatsApp
                </a> */}
              </div>

              {/* Product Hunt featured badge */}
              <a
                href="https://www.producthunt.com/products/jobscribe-4?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-jobscribe-4"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-block w-fit opacity-90 transition-opacity duration-200 hover:opacity-100"
                aria-label="JobScribe on Product Hunt"
              >
                <img
                  src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1198816&theme=dark&t=1784608377709"
                  alt="JobScribe - Streamline Your Recruitment Process | Product Hunt"
                  width={200}
                  height={43}
                  className="h-auto w-[200px] max-w-full"
                  loading="lazy"
                />
              </a>
            </div>

            {/* Menu columns — 2×2 grid on mobile, 4 columns on tablet, unchanged row on desktop */}
            <div className="grid w-full grid-cols-2 gap-x-6 gap-y-8 md:grid-cols-4 md:gap-x-6 lg:col-span-8 lg:grid-cols-4 lg:gap-x-6 lg:gap-y-0">
              {footerColumns.map((column, index) => (
                <nav
                  key={column.id}
                  className={`flex min-w-0 flex-col ${
                    index % 2 === 1 ? "max-md:justify-self-end" : ""
                  }`}
                  aria-labelledby={`footer-${column.id}`}
                >
                  <h3
                    id={`footer-${column.id}`}
                    className="mb-4 text-slate-200"
                    style={headingStyle}
                  >
                    {column.title}
                  </h3>
                  <ul className="flex flex-col gap-2.5">
                    {column.links.map((link) => {
                      const active = isActivePath(link.href);
                      return (
                        <li key={link.name}>
                          <Link
                            to={link.href}
                            className={`inline-block transition-colors duration-200 hover:text-indigo-300 ${
                              active
                                ? "font-medium text-indigo-300"
                                : "text-slate-400"
                            }`}
                            style={linkStyle}
                            aria-current={active ? "page" : undefined}
                          >
                            {link.name}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </nav>
              ))}
            </div>
          </div>

          {/* ── Bottom Bar ── */}
          <div className="border-t border-slate-700/60 pt-5">
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-between sm:gap-6">
              {/* Left: Available 24/7 */}
              <p
                className="flex items-center gap-2 text-[13px] text-slate-500 order-2 sm:order-1"
                style={{ fontFamily: '"Inter", sans-serif' }}
              >
                <Phone className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                Available 24/7
              </p>

              {/* Center: Copyright */}
              <p
                className="text-[13px] text-slate-500 order-1 sm:order-2"
                style={{ fontFamily: '"Inter", sans-serif' }}
              >
                &copy; {new Date().getFullYear()} {PLATFORM_NAME}. All rights
                reserved.
              </p>

              {/* Right: Instagram */}
              <div className="flex items-center gap-2 order-3">
                {socialLinks.map((social) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={social.name}
                      href={social.href}
                      target={social.external ? "_blank" : undefined}
                      rel={social.external ? "noopener noreferrer" : undefined}
                      className="group flex items-center gap-2 text-[13px] text-slate-500"
                      aria-label={social.ariaLabel || social.name}
                      title={social.ariaLabel || social.name}
                      style={{ fontFamily: '"Inter", sans-serif' }}
                    >
                      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-all duration-200 group-hover:bg-gradient-to-tr group-hover:from-[#F58529] group-hover:via-[#DD2A7B] group-hover:to-[#515BD4] group-hover:text-white group-hover:shadow-[0_3px_10px_rgba(221,42,123,0.28)]">
                        <Icon
                          className="h-4 w-4 transition-transform duration-200 group-hover:scale-90"
                          strokeWidth={2}
                        />
                      </span>
                      <span className="transition-colors duration-200 group-hover:bg-gradient-to-r group-hover:from-[#F58529] group-hover:via-[#DD2A7B] group-hover:to-[#515BD4] group-hover:bg-clip-text group-hover:text-transparent">
                        Follow us on Instagram
                      </span>
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
